import type { WaitlistAvailability } from '@montugroup/pms-api-contracts';
import { useCallback, useEffect, useState } from 'react';

import type { PostBookingTrigger } from '@/hooks/analytics/useConsultationWaitlistTracking';
import { useConsultationWaitlistTracking } from '@/hooks/analytics/useConsultationWaitlistTracking';
import type { BookedConsultationLookup } from '@/hooks/consultations/useFindBookedConsultation';
import { useFindBookedConsultation } from '@/hooks/consultations/useFindBookedConsultation';
import { useSubmitWaitlistPreference } from '@/pages/consultations/hooks/useSubmitWaitlistPreference';

export const JOINED_CONFIRMATION_MS = 2000;

export interface PostBookingPrompt extends BookedConsultationLookup {
  trigger: PostBookingTrigger;
}

export interface UsePostBookingWaitlistPromptOptions {
  prompt: PostBookingPrompt;
  /** gets the booked consultation's id once it's been found, so the page can move focus to its card */
  onClose: (bookedConsultationId?: number) => void;
}

type JoinOutcome = 'joined' | 'notEligible' | null;

/**
 * Finds the consultation a booking or reschedule just created and runs the post-booking waitlist prompt for it: closes
 * unseen when the patient can't join, tracks the prompt, joins, and closes after the joined confirmation.
 * @param options - the booking to look up and what to call on close
 * @returns the booked consultation and its waitlist, which part of the prompt to show, and its handlers
 */
export function usePostBookingWaitlistPrompt(options: UsePostBookingWaitlistPromptOptions) {
  const { prompt, onClose } = options;
  const [joinOutcome, setJoinOutcome] = useState<JoinOutcome>(null);

  const { status, consultation } = useFindBookedConsultation(prompt);
  const { submit, isPending, errorMessage } = useSubmitWaitlistPreference('post_booking_modal');
  const { trackPostBookingPromptShown, trackPostBookingPromptDismissed, trackPostBookingLookupTimedOut } =
    useConsultationWaitlistTracking();

  const waitlist = consultation?.waitlist;
  // OPTED_OUT can join again, like on the consultation card
  const canJoin = status === 'found' && !!waitlist?.eligible && waitlist.status !== 'ACTIVE';
  const isFormShown = canJoin && joinOutcome === null;
  const close = useCallback(() => onClose(consultation?.id), [onClose, consultation]);

  // the lookup settles once, so this shows the prompt or closes it unseen exactly once
  useEffect(() => {
    if (status === 'searching') {
      return;
    }
    if (status === 'timedOut') {
      trackPostBookingLookupTimedOut(prompt.trigger);
    }
    if (canJoin && consultation) {
      trackPostBookingPromptShown(prompt.trigger, consultation.id);
    } else {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (joinOutcome !== 'joined') {
      return undefined;
    }
    const timer = setTimeout(close, JOINED_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [joinOutcome, close]);

  const dismiss = () => {
    if (isFormShown && consultation) {
      trackPostBookingPromptDismissed(prompt.trigger, consultation.id);
    }
    close();
  };

  const join = async (availability: WaitlistAvailability) => {
    if (!consultation) {
      return;
    }
    const outcome = await submit(consultation, availability, 'join');
    if (outcome !== 'failed') {
      setJoinOutcome(outcome);
    }
  };

  return { consultation, waitlist, canJoin, isFormShown, joinOutcome, isPending, errorMessage, join, dismiss, close };
}
